/**
 * Loads `/fly`'s real region — the flown mission's own `MissionRegion`, or
 * `world/region-catalog.ts`'s `DEFAULT_FLY_REGION` (Verdun) for the instant
 * dogfight, which has no briefed region — and turns it into instanced
 * scenery recentred on that region's start aerodrome's projected position.
 *
 * `packages/engine`'s `createSim` always starts a flight at world `(0, altitudeM, 0)`
 * (`packages/engine/src/flight/state.ts` — out of this lane's scope to
 * change, and not needed: recentring the *terrain* around the chosen
 * aerodrome instead of moving the aircraft gets the same "spawn near/above
 * an aerodrome" result without touching `packages/engine`).
 */


import { buildGroundPlacements, groundHeightAt, FLAT_SURFACE } from "../world/ground-elevation.ts";
import { regionBakeFor } from "../world/region-catalog.ts";
import { loadWorldRegion } from "../world/region-loader.ts";
import type { BakedModelBody } from "../three/baked-model.ts";
import type { PlacementOrigin } from "../world/placement-transform.ts";
import { buildWorldInstances, type WorldInstances } from "../three/world-instancer.ts";
import { enhancedIndexOnce } from "../three/enhanced-catalog.ts";
import {
  createReliefField,
  withLandingFields,
  withWater,
  FLAT_RELIEF,
  type GroundQuery,
  type MissionRegion,
  type ReliefField,
  type SceneryQuery,
} from "@red-opus/engine";
import { createReliefGround, type ReliefGround } from "../three/relief-ground.ts";
import { loadRegionModels, regionGroundFrom } from "../world/region-ground.ts";
import { reliefSourceFrom } from "../world/terrain-relief-source.ts";
import { terrainFeaturesFrom, type TerrainFeatures } from "../world/terrain-features.ts";
import { sceneryQueryFor } from "../world/scenery-structures.ts";
import { createLandcoverShader } from "../world/landcover-shading.ts";
import { shadingPalette } from "./aircraft-mesh.ts";

import { buildShoreField } from "../world/shore-field.ts";
import { oceanLookFor, type OceanBrief } from "./ocean-look.ts";
import { SEA_SCALE_EXPONENT, surfaceTrianglesOf } from "../world/water-mask.ts";
import type { OceanSource } from "../three/ocean-plates.ts";

export interface FlightRegionSummary {
  readonly region: string;
  readonly startAerodrome: string;
  readonly placementCount: number;
  readonly aerodromeCount: number;
}

export interface FlightRegion {
  readonly instances: WorldInstances;
  readonly summary: FlightRegionSummary;
  /** Elevation over the region's placed `.GI` ground surfaces (the ten hills) *and* its derived relief, in the same recentred frame the scenery renders in — the sim and the scene share coordinates, so this is `step`'s `GroundQuery` directly. */
  readonly ground: GroundQuery;
  /**
   * What is *standing* on it: the region's buildings as boxes a strafing round
   * can go into (AD-018, `world/scenery-structures.ts`). `NO_SCENERY` for a
   * region that places nothing burnable. Built from the same `.GI` footprints
   * `ground` samples, so the box a round stops on is the box an undercarriage
   * would land on.
   */
  readonly scenery: SceneryQuery;
  /**
   * The region's terrain surface. `FLAT_RELIEF` for a region that has none —
   * today the two coastal maps, which need a land/sea mask first
   * (`world/terrain-relief-source.ts`).
   *
   * This is **not** gated by any setting: it feeds the sim's `GroundQuery`, so
   * it has to be identical for every peer, every replay and every census, and
   * it is derived from the region rather than stored to guarantee that.
   */
  readonly relief: ReliefField;
  /**
   * The drawn ground, or `undefined` for a region with no relief. Built here
   * rather than in the scene because everything placed has to drape onto its
   * `sampleAt` — the surface that is actually rasterised — and the meshes are
   * built in this function.
   */
  readonly surface: ReliefGround | undefined;
  /**
   * Anchors for the ENHANCED TERRAIN decoration (`three/terrain-decoration.ts`)
   * — the tree copses the original places, and the green landcover polygons
   * woods may be scattered inside. Collected here because the region's models
   * are already in hand; nothing is built until the setting asks for it.
   */
  readonly features: TerrainFeatures;
}

/** The HUD chip's two extra tags once the region has loaded: which region, and which aerodrome the flight starts near. */
export function flightRegionChips(summary: FlightRegionSummary): string[] {
  return [`${summary.region.toUpperCase()} SECTOR`, `NEAR ${summary.startAerodrome.toUpperCase()}`];
}

/**
 * The sea's material inputs, or `undefined` for a region that has none.
 *
 * **Gated on the region placing an `s = 10` plate, not on it having water.**
 * Every region has `anchorIndex` 4 faces — Verdun's ponds, the Meuse, the
 * Thames — and only London and Dunkirk carry the sea itself
 * (`world/water-mask.ts`'s `SEA_SCALE_EXPONENT`, censused in
 * `shore-field.test.ts`). A 40 m river with a 193 m swell running through it
 * would be worse than the flat blue it replaced, so the three inland maps build
 * nothing at all here and pay neither the 47 ms field nor the megabyte.
 *
 * `surfaceTrianglesOf` is called a second time (`regionGroundFrom` above already
 * used it to build the mask) rather than cached on `RegionGround`: that record
 * is cached per region for the whole session and shared with the in-flight map
 * sheet, and a quarter-megabyte of triangles held for a field only `/fly` wants
 * is the wrong trade. Measured at 12-18 ms on this box, inside the 20-35 ms the
 * region build already spends sampling its height field.
 */
function oceanSourceFor(
  body: Awaited<ReturnType<typeof loadWorldRegion>>,
  models: ReadonlyMap<string, BakedModelBody>,
  origin: PlacementOrigin,
  brief: OceanBrief | undefined,
): OceanSource | undefined {
  if (!brief) return undefined;
  const placesSea = body.placements.some((placement) => {
    const model = placement.objectRef === null ? undefined : models.get(placement.objectRef);
    return (model?.scaleExponent ?? 0) >= SEA_SCALE_EXPONENT;
  });
  if (!placesSea) return undefined;
  return { look: oceanLookFor(brief), shore: buildShoreField(surfaceTrianglesOf(body.placements, models, origin)) };
}

/**
 * `region` is the mission's own `MissionSpec.region`, or `undefined` for the
 * instant dogfight (`region-catalog.ts`'s `regionBakeFor` falls back to
 * `DEFAULT_FLY_REGION` — Verdun — either way). `viewWidthCssPx` is the view's
 * width in **CSS pixels** (`renderer.getSize()`, not the drawing buffer); the
 * region's terrain lines are sized from it so a road keeps the same
 * one-pixel-of-320 share of the view the original gives it
 * (`three/polygon-outlines.ts`).
 */
export async function loadFlightRegion(
  region?: MissionRegion,
  viewWidthCssPx?: number,
  /** The hour's walk down the palette — placed terrain is dimmed as it is built. */
  rampShift = 0,
  /**
   * ENHANCED TERRAIN's sea: the mission's own hour and sky word.
   *
   * Given them, a region that places an `s = 10` plate gets
   * `three/ocean-plates.ts`'s second material and the shoal field it reads —
   * built **whatever the switch currently says**, exactly as the enhanced
   * buildings are fetched regardless, because a flip that has to raster a shoal
   * field first would be a stall over the Channel. Omitted, which is every
   * caller with no mission to take an hour from, the plate stays the flat blue
   * quad it has always been. `rampShift` above completes the brief, so the sea,
   * the fields and the backdrop plane cannot end up at three different times of
   * day.
   */
  oceanHour?: Pick<OceanBrief, "timeOfDay" | "sky">,
): Promise<FlightRegion> {
  const entry = regionBakeFor(region);
  const body = await loadWorldRegion(entry.path);
  // The loaded bodies are handed to the instancer so each object is fetched
  // once, and to `regionGroundFrom`, which needs every placed object's geometry
  // to know where the water is before it can pick an origin that is not in it.
  const models = await loadRegionModels(body);
  const regionGround = regionGroundFrom(body, models, entry.startAerodrome);
  const origin = regionGround.origin;

  // Derived before the meshes are built, because the meshes are draped onto it.
  const source = reliefSourceFrom(body, models, origin, regionGround.aerodromes, regionGround.water);
  const relief = source ? createReliefField(source.options) : FLAT_RELIEF;

  const surface = source ? createReliefGround(relief) : undefined;
  // ENHANCED TERRAIN's field hillshading needs `BARON.PAL` to recover a ramp per
  // landcover tone. Same document-shared fetch the aeroplanes' ramp material
  // uses, so this costs nothing extra; if it fails the fields simply stay flat,
  // which is the same "a missing palette costs the refinement, not the picture"
  // fallback `aircraft-mesh.ts` takes.
  const palette = surface ? await shadingPalette() : undefined;
  const landcoverShade = palette
    ? createLandcoverShader(palette.colors.map(([r, g, b]) => ((r << 16) | (g << 8) | b) >>> 0))
    : undefined;
  // Hoisted above the instancer because a wood is now *two* things: the trees
  // `scene-terrain.ts` scatters, and G20's canopy ground paint, which the
  // instancer lays into the landcover plates as it drapes them. Both read this
  // one list, which is what makes "the paint is under the trees" structural.
  const features = terrainFeaturesFrom(body, models, origin);
  // ENHANCED MODELS for the buildings. Fetched whatever the switch currently
  // says, for the same reason the LOD ladder rides in one file: the setting is a
  // toggle a player flips mid-sortie, and a swap that has to fetch first is a
  // stall over a town. It is 1.1-1.8 MB a region against an airframe's 1.5, and
  // `/enhanced` is served compressed. An install that never ran the tool gets an
  // empty index and every building draws its bake, which is what it always did.
  const enhancedModels = await enhancedIndexOnce().catch(() => undefined);
  const ocean = oceanSourceFor(body, models, origin, oceanHour && { ...oceanHour, rampShift });
  const instances = await buildWorldInstances(body.placements, origin, {
    viewWidthCssPx,
    models,
    rampShift,
    woodSites: features.woodSites,
    ...(enhancedModels && enhancedModels.size > 0 ? { enhancedModels } : {}),
    // `drapeAt`, not `sampleAt`: the drape must land a datum-placed thing the
    // flat world's 8 cm *above* the drawn surface, not exactly on it.
    ...(surface ? { ground: surface.drapeAt, groundLattice: surface.lattice } : {}),
    ...(landcoverShade ? { landcoverShade } : {}),
    ...(ocean ? { ocean } : {}),
  });
  const groundPlacements = buildGroundPlacements(body, origin);

  return {
    instances,
    relief,
    surface,
    features,
    // Elevation from `.GI` plus the derived relief, with the region's own
    // aerodromes stamped as prepared ground and its sea stamped as sea. All
    // three matter to an undercarriage: `terrain-relief.ts` grades the apron
    // *flat*, `withLandingFields` is what makes the sim treat it as an
    // aerodrome rather than as a farmer's field — `flight/ground.ts`'s
    // `SURFACE_GEAR_LIMIT_SCALE` is 1.0 against 0.8, i.e. an arrival that a
    // prepared strip survives can break a gear leg on open country — and
    // `withWater` is what makes the North Sea a ditching rather than a landing
    // (gear limit 0). Water is outermost: nothing is *emplaced* wet any more,
    // but where the aeroplane goes is the player's business.
    ground: withWater(
      withLandingFields(
        // `surface.drapeAt`, **not** `relief.heightAt`: the wheels have to touch
        // the surface that is rasterised, which is the one every plate, road and
        // dropped building was draped through a few lines up. Handing the sim
        // the analytic field instead is what made a landing on a road fall
        // through it — `world/ground-elevation.ts`'s `groundHeightAt` has the
        // measured numbers. A region with no relief has no drawn mesh either,
        // and its datum is flat, so `FLAT_SURFACE` is the same answer.
        { groundHeightAt: (x, z) => groundHeightAt(groundPlacements, x, z, surface?.drapeAt ?? FLAT_SURFACE) },
        source?.aerodromeZones.map((zone) => ({ x: zone.x, z: zone.z, radiusM: zone.radiusM })) ?? [],
      ),
      (x, z) => regionGround.water.isWater(x, z),
    ),
    // Same `drapeAt` sampler the meshes were placed with, so a fire box's floor
    // is the height the building is actually drawn standing at.
    scenery: sceneryQueryFor(body, origin, surface?.drapeAt),
    summary: {
      region: body.region,
      startAerodrome: entry.startAerodrome,
      placementCount: body.placements.length,
      aerodromeCount: body.aerodromes.length,
    },
  };
}
